import { Instance, SnapshotIn, types, flow, onSnapshot, getEnv, getSnapshot } from 'mobx-state-tree';
import { DataState, DataStateStore } from '@ace/core';

import { IStoresEnv } from '@core/storesEnv';
import { USERS_IN_GROUP_ID, ALL_USERS_ID } from '@shared/constants';
import { ExecutionsGridStore } from './ExecutionsGrid.store';
import { IGroupsDataStoreSnaphotOut, GroupUpdate$ } from 'Groups/store';
import { IPageQueryStoreSnapshotIn } from './PageQuery.store';
import { performGetGradings } from 'NeoAnalytics/services/getGradings';
import { performQueryActors } from 'NeoAnalytics/services/getActor';

type KnownProcedure = {
    id: string;
    name: string;
};

type ExecutionGradingSummary = {
    isGraded: boolean;
    isReviewed: boolean;
    reviewCount: number;
};

type ProcedureActorOption = {
    id: string;
    label: string;
    count: number;
};

type ExecutionLike = {
    id: string;
    entityId: string | null;
    entityName: string | null;
    actorIds: string[];
    startTime: string | null;
    totalSteps: number | null;
    steps: Array<{
        actorId?: string | null;
        duration: string | null;
        startTime: string;
    }>;
    events: Array<{
        actorId?: string | null;
        timestamp: string | null;
        description: string | null;
    }>;
};

function getExecutionActorIds(execution: Pick<ExecutionLike, 'actorIds' | 'steps' | 'events'>): string[] {
    const actorIds = [
        ...(execution.actorIds ?? []),
        ...(execution.steps ?? []).map(step => step.actorId ?? ''),
        ...(execution.events ?? []).map(event => event.actorId ?? ''),
    ].filter((actorId): actorId is string => Boolean(actorId));

    return Array.from(new Set(actorIds));
}

function sortExecutionsByStartTimeDescending<T extends { startTime: string | null }>(left: T, right: T): number {
    return new Date(right.startTime ?? 0).getTime() - new Date(left.startTime ?? 0).getTime();
}

function hasExecutedSteps(execution: { steps: Array<unknown> }): boolean {
    return execution.steps.length > 0;
}

export function durationToSeconds(s: string|null): number|null {
    if(s){
        const m = s.match(/^(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
        if (!m) {throw new Error(`Invalid duration format: ${s}`);}

        const hours = Number(m[1]);
        const minutes = Number(m[2]);
        const seconds = Number(m[3]);

        const frac = m[4] ?? "0";
        const fractionalSeconds = Number(`0.${frac}`);

        return hours * 3600 + minutes * 60 + seconds + fractionalSeconds;
    }

    return null;
}

export const ExecutionsGridUIStoreInferred = types
    .model('ExecutionsGridUIStoreInferred', {
        _execuitions: types.optional(ExecutionsGridStore, {}),
        analyticsActorId: types.maybeNull(types.string),
        analyticsEntityId: types.maybeNull(types.string),
        isExporting: types.optional(types.boolean, false),
        isImporting: types.optional(types.boolean, false),
        isActivePage: types.optional(types.boolean, false),
    })
    .volatile(() => ({
        status: DataStateStore.create({ state: DataState.initial }),
        knownProcedures: [] as KnownProcedure[],
        gradingSummariesByExecutionId: {} as Record<string, ExecutionGradingSummary>,
        actorLabelsById: {} as Record<string, string>,
        scopedEntityIds: null as string[] | null,
        executionsLoading: false,
        actorsLoading: false,
        gradingsLoading: false,
    }))
    .views(self => ({
        get executions() {
            const baseExecutions = self._execuitions.executionsList;
            const executions = self.analyticsEntityId && self.analyticsActorId
                ? baseExecutions.filter(execution => getExecutionActorIds(execution).includes(self.analyticsActorId!))
                : baseExecutions;

            if (self.scopedEntityIds === null) {
                return executions;
            }

            const scopedIds = new Set(self.scopedEntityIds);

            return executions.filter(execution => scopedIds.has(execution.entityId ?? ''));
        },
        get knownProcedureOptions() { return self.knownProcedures; },
        get gradingSummaries() { return self.gradingSummariesByExecutionId; },
        get actorLabels() { return self.actorLabelsById; },
        get isExecutionsLoading() { return self.executionsLoading; },
        get isActorsLoading() { return self.actorsLoading; },
        get isGradingsLoading() { return self.gradingsLoading; },
        get procedureActorOptions() {
            if (!self.analyticsEntityId) {
                return [] as ProcedureActorOption[];
            }

            const procedureExecutions = self._execuitions.executionsList
                .map(execution => getSnapshot(execution)) as ExecutionLike[];
            const counts = new Map<string, number>();

            procedureExecutions.forEach(execution => {
                if (execution.entityId !== self.analyticsEntityId) {
                    return;
                }

                getExecutionActorIds(execution).forEach(actorId => {
                    if (!actorId) {
                        return;
                    }

                    counts.set(actorId, (counts.get(actorId) ?? 0) + 1);
                });
            });

            return Array.from(counts.entries())
                .map(([id, count]) => ({
                    id,
                    count,
                    label: self.actorLabelsById[id] || 'Unknown actor',
                }))
                .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));
        },
    }))
    .views(self => ({
        get allSelectedProcedureExecutions() {
            const executions = self._execuitions.executionsList.map(execution => getSnapshot(execution)) as ExecutionLike[];

            if (!self.analyticsEntityId) {
                return [];
            }

            return executions
                .filter(execution => execution.entityId === self.analyticsEntityId)
                .sort(sortExecutionsByStartTimeDescending);
        },
    }))
    .views(self => ({
        get selectedProcedureExecutions() {
            if (!self.analyticsActorId) {
                return self.allSelectedProcedureExecutions;
            }

            return self.allSelectedProcedureExecutions.filter(execution => getExecutionActorIds(execution).includes(self.analyticsActorId!));
        },
    }))
    .views(self => ({
        get chartExecutions() {
            return self.selectedProcedureExecutions
                .filter(hasExecutedSteps)
                .slice(0, 5);
        },
    }))
    .views(self => ({
        get formattedExecutions() {
            const filtered = self.chartExecutions as ExecutionLike[];

            const procedureSteps: Record<string, any>[] = [];

            const totalSteps = filtered.reduce((max: number, execution: ExecutionLike) => {
                const executionStepCount = Math.max(execution.totalSteps ?? 0, execution.steps.length);

                return Math.max(max, executionStepCount);
            }, 0);

            for(let i = 0; i < totalSteps; i++){
                const stepData: Record<string, any> = {
                    index: (i + 1),
                };
                procedureSteps.push(stepData);
            }

            const formattedSteps = procedureSteps.map((step, stepI) => {
                const stepData: Record<string, any> = {
                    index: step.index,
                };

                filtered.map((exe: ExecutionLike, exeI: number) =>{
                    if(exe.steps.length != 0){
                        exe.steps.map((st: ExecutionLike['steps'][number], i: number) =>{
                            if(i === stepI){
                                if(st.duration){
                                    const duration = durationToSeconds(st.duration);
                                    stepData[`exe${exeI+1}`]={
                                        time: duration,
                                    }
                                    const stepEv = exe.events.find((evo: ExecutionLike['events'][number]) => evo.timestamp == st.startTime);
                                    if(stepEv){
                                        stepData[`exeEvo${exeI+1}`]={
                                            time: duration,
                                            description: stepEv.description,
                                        }
                                    }
                                }
                            }
                        })
                    }
                })

                return stepData;
            });

            const initialValue: Record<string, any> =  { index: ''};

            filtered.map((exe: ExecutionLike, i: number) =>{
                if(exe){
                    initialValue[`exe${i+1}`]={
                        time: 0,
                    }
                }
            })

            formattedSteps.unshift(initialValue);

            return formattedSteps;
        },
        get pagination() { return self._execuitions.paginationStatus; },
        get requestParams() { return self._execuitions.requestParams; },
        get isFilterOrSearchApplied() { return self._execuitions.requestParams.areSearchParamsSet; },
        get executionsFilters() { return self._execuitions.executionsFilterOptions; },
        isActive(id: string) {
            return self.analyticsEntityId === id;
        },
    }))
    .views(self => ({
        get isTableVisible() { return self.executions.length || self.status.isLoading; },
        get noFilteringResults() { return !self.executions.length && !self.status.isLoading && self.isFilterOrSearchApplied },
        get noItemsInGroup() { return !self.executions.length && !self.status.isLoading && !self.isFilterOrSearchApplied },
    }))
    .actions(self => {
        const { api } = getEnv<IStoresEnv>(self);
        let activeLoadSignature: string | null = null;
        let activeLoadPromise: Promise<void> | null = null;

        const getLoadSignature = () => JSON.stringify({
            requestParams: self.requestParams.getParams,
            analyticsActorId: self.analyticsActorId,
            analyticsEntityId: self.analyticsEntityId,
            scopedEntityIds: self.scopedEntityIds,
        });

        const syncKnownProcedures = () => {
            const merged = new Map<string, KnownProcedure>(self.knownProcedures.map(procedure => [procedure.id, procedure]));

            self._execuitions.executionsList.forEach(execution => {
                if (execution.entityId && execution.entityName) {
                    merged.set(execution.entityId, {
                        id: execution.entityId,
                        name: execution.entityName,
                    });
                }
            });

            self.knownProcedures = Array.from(merged.values());
        };

        let latestGradingSummariesRequestId = 0;

        const loadGradingSummaries = flow(function* (executionIds: string[]) {
            const requestId = ++latestGradingSummariesRequestId;
            const uniqueExecutionIds = Array.from(new Set(executionIds.filter(Boolean)));

            if (!uniqueExecutionIds.length) {
                self.gradingSummariesByExecutionId = {};

                return;
            }

            const fallbackSummaries = uniqueExecutionIds.reduce<Record<string, ExecutionGradingSummary>>((accumulator, executionId) => {
                accumulator[executionId] = {
                    isGraded: false,
                    isReviewed: false,
                    reviewCount: 0,
                };

                return accumulator;
            }, {});

            const result = yield performGetGradings(
                api,
                {
                    payload: { executionIds: uniqueExecutionIds },
                    errorHandlers: { DEFAULT: '' },
                },
            );

            if (requestId !== latestGradingSummariesRequestId) {
                return;
            }

            if (!result.success || !Array.isArray(result.data)) {
                self.gradingSummariesByExecutionId = fallbackSummaries;

                return;
            }

            const summaries = { ...fallbackSummaries };

            result.data.forEach((grading: any) => {
                const executionId = String(grading?.executionId ?? '');

                if (!executionId || !summaries[executionId]) {
                    return;
                }

                const reviewCount = Array.isArray(grading?.reviews) ? grading.reviews.length : 0;

                summaries[executionId] = {
                    isGraded: true,
                    isReviewed: Boolean(grading?.isReviewed) || reviewCount > 0,
                    reviewCount,
                };
            });

            self.gradingSummariesByExecutionId = summaries;
        });

        const loadActorLabels = flow(function* (actorIds: string[]) {
            const uniqueActorIds = Array.from(new Set(actorIds.filter(Boolean)));

            if (!uniqueActorIds.length) {
                self.actorLabelsById = {};

                return;
            }

            const fallbackLabels = uniqueActorIds.reduce<Record<string, string>>((accumulator, actorId) => {
                accumulator[actorId] = '';

                return accumulator;
            }, {});

            const result = yield performQueryActors(
                api,
                {
                    payload: { ActorIds: uniqueActorIds },
                    errorHandlers: { DEFAULT: '' },
                },
            );

            if (!result.success) {
                self.actorLabelsById = fallbackLabels;

                return;
            }

            const actors = Array.isArray(result.data)
                ? result.data
                : (Array.isArray(result.data?.entities) ? result.data.entities : []);

            const labels = { ...fallbackLabels };

            actors.forEach((actor: any) => {
                const actorId = String(actor?.id ?? actor?.actorId ?? '');

                if (!actorId) {
                    return;
                }

                const displayName = actor?.displayName
                    ?? [actor?.firstName, actor?.lastName].filter(Boolean).join(' ')
                    ?? actor?.email
                    ?? actorId;

                labels[actorId] = displayName;
            });

            self.actorLabelsById = labels;
        });

        const loadInternal = flow(function* () {
            self.status.setLoading();
            self.executionsLoading = true;
            self.actorsLoading = false;
            self.gradingsLoading = false;
            yield self._execuitions.loadExecutions();
            self.executionsLoading = false;
            syncKnownProcedures();
            self.status.setDone();

            const executionIds = self._execuitions.executionsList.map(execution => execution.id);
            const actorIds = self._execuitions.executionsList.flatMap(execution => getExecutionActorIds(getSnapshot(execution) as ExecutionLike));
            const shouldLoadActorLabels = !self.analyticsActorId && actorIds.length > 0;

            self.actorsLoading = shouldLoadActorLabels;
            self.gradingsLoading = executionIds.length > 0;

            if (!shouldLoadActorLabels) {
                self.actorLabelsById = {};
            }

            try {
                yield Promise.all([
                    shouldLoadActorLabels ? loadActorLabels(actorIds) : Promise.resolve(),
                    loadGradingSummaries(executionIds),
                ]);
            } finally {
                self.actorsLoading = false;
                self.gradingsLoading = false;
            }
        });

        const load = () => {
            const signature = getLoadSignature();

            if (activeLoadPromise && activeLoadSignature === signature) {
                return activeLoadPromise;
            }

            activeLoadSignature = signature;

            const nextPromise = Promise.resolve(loadInternal()).finally(() => {
                if (activeLoadPromise === nextPromise) {
                    activeLoadPromise = null;
                    activeLoadSignature = null;
                }
            });

            activeLoadPromise = nextPromise;

            return nextPromise;
        };

        const setParams = (params: IPageQueryStoreSnapshotIn) => {
            self.requestParams.setParams(params);
            self.requestParams.saveParamsToStorage();
        }

        const resetParams = () => self.requestParams.resetParams();

        const togglePageActivity = (isActive: boolean) => {
            const wasActive = self.isActivePage;
            self.isActivePage = isActive;

            if (isActive && !wasActive) {
                void load();
            }
        };

        const clearData = () => {
            activeLoadSignature = null;
            activeLoadPromise = null;
            resetParams();
            self._execuitions.cleanData();
            self.knownProcedures = [];
            self.gradingSummariesByExecutionId = {};
            self.actorLabelsById = {};
            self.scopedEntityIds = null;
            self.executionsLoading = false;
            self.actorsLoading = false;
            self.gradingsLoading = false;
            clearAnalyticsActorId();
            clearAnalyticsEntityId();
        }

        const setAnalyticsActorId = (id: string | null) => {
            self.analyticsActorId = id;
        }

        const clearAnalyticsActorId = () => self.analyticsActorId = null;

        const setAnalyticsEntityId = (id: string | null) => {
            self.analyticsEntityId = id;
        }

        const clearAnalyticsEntityId = () => self.analyticsEntityId = null;

        const setScopedEntityIds = (entityIds: string[] | null) => {
            self.scopedEntityIds = entityIds ? Array.from(new Set(entityIds)) : null;
        };

        const clearScopedEntityIds = () => {
            self.scopedEntityIds = null;
        };

        return {
            load,
            setParams,
            resetParams,
            togglePageActivity,
            clearData,
            syncKnownProcedures,
            loadGradingSummaries,
            loadActorLabels,
            setAnalyticsActorId,
            clearAnalyticsActorId,
            setAnalyticsEntityId,
            clearAnalyticsEntityId,
            setScopedEntityIds,
            clearScopedEntityIds,
        }
    })
    .actions(self => {
        const { groups } = getEnv<IStoresEnv>(self);

        return {
            afterCreate: () => {
                GroupUpdate$.subscribe(() => {
                    if (self.isActivePage) {
                        self.load();
                    }
                });

                onSnapshot(self.requestParams, () => {
                    if (self.isActivePage) {
                        self.load();
                    }
                    self.requestParams.saveParamsToStorage();
                });

                onSnapshot(groups, (snap: IGroupsDataStoreSnaphotOut) => {
                    if (snap.selectedGroup) {
                        self.setParams({
                            page: 0,
                            inGroup: USERS_IN_GROUP_ID,
                            inGroupId: snap.selectedGroup as string,
                        });
                    }

                    // Show 0 page of 'All users' after group removing
                    if (self.requestParams.inGroup === USERS_IN_GROUP_ID && !snap.selectedGroup) {
                        self.setParams({
                            page: 0,
                            inGroup: ALL_USERS_ID,
                            inGroupId: undefined,
                        });
                    }
                })
            },
            beforeDestroy: () => {
            },
        }
    })

type ExecutionsGridUIStoreFactoryType = typeof ExecutionsGridUIStoreInferred;
interface IExecutionsGridUIStoreFactory extends ExecutionsGridUIStoreFactoryType { }
export const ExecutionsGridUIStore: IExecutionsGridUIStoreFactory = ExecutionsGridUIStoreInferred;
export interface IExecutionsGridUIStore extends Instance<IExecutionsGridUIStoreFactory> { }
export interface IExecutionsGridUIStoreSnapshotIn extends SnapshotIn<IExecutionsGridUIStore> { }
